/**
 * 振込先を銀行口座にしてもらう促し（受け取りの登録・出金の画面）。
 *
 * WHY: Stripe はデビットカードからマイナス残高を引き落とせない（apps/server/src/modules/money/connect.ts の createOnboardingSession の注記）。
 * 出金した後に返金・チャージバックが来て残高が負になったとき、銀行口座なら自動で引き落として埋まるが、カードだけだと埋まらず
 * 作者の請求残（次の売上から相殺）として残り続ける。Stripe の登録フォームは「銀行口座だけ」に絞れないので、画面で促す。
 *
 * 振込先の種類はサーバーの GET /v1/money/payee が**まだ返さない**（2026-09-27）。今は bankAccountPrompt が 'advise' を返し、
 * 押し付けない一般の案内を出す。サーバーが payee.payoutMethod を返すようになれば、口座が無い / カードだけの人にだけ強い表示に変わる
 * （この部品の変更は要らない）。
 */
import { Link } from 'react-router-dom';
import { useResource } from '../../hooks';
import { useT } from '../../i18n';
import type { PayeeView } from '../../moneyApi';
import { bankAccountPrompt } from '../../moneyLogic';

export function BankAccountNotice({
  livemode,
  mode,
  payee,
  compact = false,
  hideAction = false
}: {
  livemode: boolean;
  mode: 'live' | 'test';
  /** 呼び出し側が受取人を読んでいれば渡す（同じ API を 2 回呼ばない）。渡さなければここで読む */
  payee?: PayeeView['payee'];
  /** 出金の画面など = 一般の案内（advise）は 1 行の控えめな表示にする */
  compact?: boolean;
  /** 受け取りの登録の画面そのものでは「登録を開く」のボタンが同じ画面にあるので出さない */
  hideAction?: boolean;
}) {
  const { t } = useT();
  const own = useResource<PayeeView>(payee === undefined ? `/v1/money/payee?livemode=${livemode}` : null);
  const p = payee === undefined ? (own.data?.payee ?? null) : payee;
  const prompt = bankAccountPrompt(p, p?.payoutMethod);
  if (!prompt) return null;

  if (prompt === 'advise') {
    return compact ? (
      <p className="muted small" role="note">
        {t('bank.adviseShort')}
      </p>
    ) : (
      <div className="alert alert-info" role="note">
        <strong>{t('bank.adviseTitle')}</strong>
        <span>{t('bank.adviseBody')}</span>
      </div>
    );
  }
  return (
    <div className="alert alert-warn" role="note">
      <strong>{t(prompt === 'add_bank' ? 'bank.addTitle' : 'bank.replaceTitle')}</strong>
      <span>{t('bank.whyBody')}</span>
      {!hideAction && (
        <Link to={`/money/${mode}/get-paid`} className="btn btn-ghost btn-sm">
          {t('bank.action')}
        </Link>
      )}
    </div>
  );
}
